import { useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Square, Loader2, Github } from "lucide-react";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Button } from "./ui/button";
import { ACTIVITY_TYPE_OPTIONS } from "../lib/constants";

function cloneAttachments(list) {
  return (list || []).map((a) => ({ ...a, included: a.included !== false }));
}

/**
 * Confirmation dialog shown before stopping a running timer.
 *
 * onConfirm(description, activityType, attachments, endTimeIso|null)
 */
const TimerStopDialog = ({
  open,
  elapsed,
  workOrder,
  initialDescription,
  notes = [],
  githubAttachments = [],
  loading = false,
  onCancel,
  onConfirm,
}) => {
  const [description, setDescription] = useState("");
  const [activityType, setActivityType] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [addSha, setAddSha] = useState("");
  const [addPr, setAddPr] = useState("");
  const [endLocal, setEndLocal] = useState(""); // datetime-local; empty = now

  useEffect(() => {
    if (open) {
      setDescription(initialDescription || "");
      setActivityType("");
      setAttachments(cloneAttachments(githubAttachments));
      setAddSha("");
      setAddPr("");
      setEndLocal("");
    }
  }, [open, initialDescription, githubAttachments]);

  const toggleIncluded = (idx) => {
    setAttachments((prev) => prev.map((a, i) => (i === idx ? { ...a, included: !a.included } : a)));
  };

  const removeAt = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const addManual = () => {
    const next = [...attachments];
    if (addSha.trim()) {
      next.push({
        kind: "commit",
        repo: workOrder?.github_repo || "",
        sha: addSha.trim().slice(0, 40),
        message: "",
        included: true,
        delivery_id: `manual-${Date.now()}`,
        event_at: new Date().toISOString(),
      });
      setAddSha("");
    }
    if (addPr.trim()) {
      if (!addPr.includes("github.com") || !addPr.includes("/pull/")) {
        return;
      }
      next.push({
        kind: "pull_request",
        repo: workOrder?.github_repo || "",
        pr_url: addPr.trim(),
        included: true,
        delivery_id: `manual-pr-${Date.now()}`,
        event_at: new Date().toISOString(),
      });
      setAddPr("");
    }
    setAttachments(next);
  };

  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && !loading && onCancel()}>
      <AlertDialogContent
        className="max-h-[90vh] overflow-y-auto"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Square className="w-4 h-4 text-brand-ink" />
            Stop timer?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <div className="rounded-md border border-brand-border bg-brand-subtle p-3 space-y-1">
                {workOrder?.title && (
                  <p className="font-semibold text-brand-ink text-base">{workOrder.title}</p>
                )}
                <p className="text-brand-steel">
                  Elapsed: <span className="font-mono font-medium text-brand-ink">{elapsed}</span>
                </p>
              </div>

              {notes.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs text-brand-steel">Session notes ({notes.length})</Label>
                  <div className="max-h-32 overflow-y-auto rounded-md border border-brand-border bg-brand-subtle p-2 space-y-1">
                    {notes.map((note, i) => (
                      <div key={i} className="flex gap-2 text-xs">
                        <span className="font-mono text-brand-steel shrink-0">
                          {(() => { try { return new Date(note.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return "--:--"; } })()}
                        </span>
                        <span className="text-brand-steel">{note.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {attachments.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs text-brand-steel flex items-center gap-1">
                    <Github className="w-3 h-3" /> GitHub during this timer
                  </Label>
                  <p className="text-xs text-brand-steel">Uncheck items that belong to another job. Hours still come from the timer, not git.</p>
                  <div className="space-y-1 rounded-md border border-brand-border p-2">
                    {attachments.map((att, i) => (
                      <label key={`${att.sha || att.pr_url || i}`} className="flex items-start gap-2 text-xs">
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={att.included !== false}
                          onChange={() => toggleIncluded(i)}
                          disabled={loading}
                        />
                        <span className="flex-1 min-w-0">
                          <span className="font-mono text-brand-ink">
                            {att.kind === "pull_request" ? (att.pr_url || "PR") : (att.sha || "").slice(0, 7)}
                          </span>
                          {att.message ? <span className="text-brand-steel"> — {att.message}</span> : null}
                          {att.repo ? <span className="block text-brand-steel">{att.repo}</span> : null}
                        </span>
                        <button type="button" className="text-brand-steel hover:text-brand-ink" onClick={() => removeAt(i)} disabled={loading}>
                          ✕
                        </button>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <Label className="text-xs text-brand-steel">Add commit / PR if the webhook missed</Label>
                <div className="flex gap-2">
                  <Input
                    value={addSha}
                    onChange={(e) => setAddSha(e.target.value)}
                    placeholder="Commit SHA"
                    className="h-8 text-xs font-mono"
                    disabled={loading}
                  />
                  <Input
                    value={addPr}
                    onChange={(e) => setAddPr(e.target.value)}
                    placeholder="https://github.com/org/repo/pull/1"
                    className="h-8 text-xs"
                    disabled={loading}
                  />
                  <Button type="button" variant="outline" size="sm" onClick={addManual} disabled={loading || (!addSha.trim() && !addPr.trim())}>
                    Add
                  </Button>
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-brand-steel">Description (optional)</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What did you work on?"
                  className="border-brand-border text-sm"
                  rows={2}
                  disabled={loading}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="timer-stop-end-time" className="text-xs text-brand-steel">
                  Actual end time <span className="text-brand-steel">(optional — blank = now)</span>
                </Label>
                <Input
                  id="timer-stop-end-time"
                  type="datetime-local"
                  value={endLocal}
                  onChange={(e) => setEndLocal(e.target.value)}
                  className="h-8 text-xs border-brand-border"
                  disabled={loading}
                  data-testid="timer-stop-end-time"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-brand-steel">
                  Activity Type <span className="text-brand-steel">(optional)</span>
                </Label>
                <Select value={activityType} onValueChange={setActivityType} disabled={loading}>
                  <SelectTrigger className="border-brand-border">
                    <SelectValue placeholder="Select activity..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIVITY_TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel} disabled={loading}>Keep running</AlertDialogCancel>
          <Button
            onClick={() => {
              let endTimeIso = null;
              if (endLocal) {
                const parsed = new Date(endLocal);
                if (Number.isNaN(parsed.getTime())) {
                  return;
                }
                endTimeIso = parsed.toISOString();
              }
              onConfirm(description, activityType, attachments, endTimeIso);
            }}
            disabled={loading}
            className="bg-brand-ink hover:bg-brand-ink/90 text-brand-on-ink"
          >
            {loading ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <Square className="w-3.5 h-3.5 mr-1.5" />
            )}
            {loading ? "Stopping…" : "Stop & log time"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default TimerStopDialog;
